import React, { useEffect, useState } from "react";
import { AlertCircle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { cn } from "@/lib/utils";
import type { AuthViewModelReturn } from "@/viewmodels/auth/useAuthViewModel";

interface OtpStepProps {
  vm: AuthViewModelReturn;
}

const OTP_SLOT_COUNT = 6;

/** Shared OTP verification step used by the login and booking auth flows. */
export const OtpStep: React.FC<OtpStepProps> = ({ vm }) => {
  const [shaking, setShaking] = useState(false);
  const hasError = Boolean(vm.otpError);

  useEffect(() => {
    if (vm.otpShakeKey === 0 || !vm.otpError) return;

    setShaking(true);
    const timer = window.setTimeout(() => setShaking(false), 450);
    return () => window.clearTimeout(timer);
  }, [vm.otpShakeKey, vm.otpError]);

  const slotClassName = cn(
    hasError &&
      "border-red-400 bg-red-50/60 text-red-700 ring-1 ring-red-200/80"
  );

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-green-100 to-blue-100">
          <Phone className="h-8 w-8 text-green-600" />
        </div>
        <p className="text-gray-600">We&apos;ve sent a 6-digit code to</p>
        <p className="font-semibold text-gray-900">
          {vm.selectedCountry?.callingCode} {vm.phoneNumber}
        </p>
      </div>

      <div>
        <label className="mb-4 block text-center text-sm font-medium text-gray-700">
          Enter Verification Code
        </label>
        <div
          className={cn("flex justify-center", shaking && "animate-shake")}
          aria-invalid={hasError}
          aria-describedby={hasError ? "otp-error" : undefined}
        >
          <InputOTP
            maxLength={OTP_SLOT_COUNT}
            value={vm.otp}
            onChange={(val) => vm.setOtp(val.replace(/[^0-9]/g, ""))}
            className="gap-2"
          >
            <InputOTPGroup>
              {Array.from({ length: OTP_SLOT_COUNT }, (_, index) => (
                <InputOTPSlot
                  key={index}
                  index={index}
                  className={slotClassName}
                />
              ))}
            </InputOTPGroup>
          </InputOTP>
        </div>

        {hasError && (
          <p
            id="otp-error"
            role="alert"
            className="mt-3 flex items-center justify-center gap-1.5 text-center text-sm font-medium text-red-600 animate-in fade-in slide-in-from-top-1 duration-200"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            {vm.otpError}
          </p>
        )}
      </div>

      <Button
        onClick={vm.handleOtpSubmit}
        disabled={vm.loading}
        className="w-full bg-gradient-to-r from-blue-600 to-green-500 py-3 hover:from-blue-700 hover:to-green-600"
      >
        {vm.loading ? "Verifying..." : "Verify & Continue"}
      </Button>

      <div className="text-center">
        <p className="text-gray-600">
          Didn&apos;t receive the code?
          <button
            type="button"
            className="ml-1 font-semibold text-blue-600 hover:underline"
            onClick={vm.handleResendOtp}
            disabled={vm.loading}
          >
            Resend OTP
          </button>
        </p>
      </div>
    </div>
  );
};
